import { z } from 'zod';

import {
  LEGAL_DOCUMENT_STATUSES,
  LEGAL_DOCUMENT_TYPES,
} from '../legal.js';

import { isoDateTimeSchema, uuidSchema } from './common.js';

const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Неверная дата').nullable();

export const legalDocumentTypeSchema = z.enum(LEGAL_DOCUMENT_TYPES);
export const legalDocumentStatusSchema = z.enum(LEGAL_DOCUMENT_STATUSES);

const legalDocumentCoreSchema = z.object({
  id: uuidSchema,
  type: legalDocumentTypeSchema,
  clientId: uuidSchema.nullable(),
  clientName: z.string().nullable(),
  parentId: uuidSchema.nullable(),
  number: z.string().min(1).max(64),
  title: z.string().min(1).max(200),
  status: legalDocumentStatusSchema,
  signedOn: isoDateSchema,
  validFrom: isoDateSchema,
  validTo: isoDateSchema,
  notes: z.string().max(8000).nullable(),
  fileName: z.string().max(255).nullable(),
  fileMimeType: z.string().max(128).nullable(),
  fileSizeBytes: z.number().int().nonnegative().nullable(),
  fileUploadedAt: isoDateTimeSchema.nullable(),
  hasFile: z.boolean(),
  isExpired: z.boolean(),
  expiresInDays: z.number().int().nullable(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});

export const legalDocumentSchema = legalDocumentCoreSchema.extend({
  annexes: z.array(legalDocumentCoreSchema).optional(),
});
export type LegalDocumentDto = z.infer<typeof legalDocumentSchema>;

const legalDocumentBodyBase = z.object({
  type: legalDocumentTypeSchema,
  clientId: uuidSchema.nullable().optional(),
  parentId: uuidSchema.nullable().optional(),
  number: z.string().trim().min(1).max(64),
  title: z.string().trim().min(1).max(200),
  status: legalDocumentStatusSchema.optional(),
  signedOn: isoDateSchema.optional(),
  validFrom: isoDateSchema.optional(),
  validTo: isoDateSchema.optional(),
  notes: z.string().max(8000).nullable().optional(),
});

export const createLegalDocumentSchema = legalDocumentBodyBase;
export type CreateLegalDocumentDto = z.infer<typeof createLegalDocumentSchema>;

export const updateLegalDocumentSchema = legalDocumentBodyBase.partial().refine(
  (d) => Object.keys(d).length > 0,
  { message: 'Нет данных для обновления' },
);
export type UpdateLegalDocumentDto = z.infer<typeof updateLegalDocumentSchema>;

export const legalDocumentListResponseSchema = z.object({
  items: z.array(legalDocumentSchema),
  expiring: z.array(legalDocumentSchema),
});
export type LegalDocumentListResponseDto = z.infer<typeof legalDocumentListResponseSchema>;

/** Normalize nullable ids from API payloads. */
export function refineLegalDocumentBody(
  body: CreateLegalDocumentDto | UpdateLegalDocumentDto,
): {
  type?: LegalDocumentDto['type'];
  clientId: string | null;
  parentId: string | null;
  validFrom: string | null;
  validTo: string | null;
} {
  const type = 'type' in body ? body.type : undefined;
  const clientId =
    body.clientId === undefined || body.clientId === '' ? null : (body.clientId ?? null);
  const parentId =
    body.parentId === undefined || body.parentId === '' ? null : (body.parentId ?? null);
  return {
    type,
    clientId,
    parentId,
    validFrom: body.validFrom ?? null,
    validTo: body.validTo ?? null,
  };
}
